import {
  CORE_VERSION,
  SCHEMA_VERSION,
  toIso8601,
  wallClockOf,
  type BackupHealth,
  DEFAULT_PREFERENCES,
  readPreferences,
  writePreferences,
  type Preferences,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useBackupHealth } from '../backup/useBackupHealth.js'
import { Button } from '../components/Button.js'
import { ListRow } from '../components/ListRow.js'
import { Toggle } from '../components/Toggle.js'
import {
  describePersistence,
  formatBytes,
  type StorageStatus,
} from '../pwa/persistence.js'
import { useStorageStatus } from '../pwa/useStorageStatus.js'
import { useServices } from '../services/ServicesProvider.js'
import styles from './SettingsScreen.module.css'

/**
 * Settings.
 *
 * The device block is not filler. This app stores `{ epochMs, timeZone }`
 * rather than a bare instant precisely because time-of-day analysis breaks
 * across DST changes and travel (ADR-0006), so what zone the device reports is
 * a real diagnostic — it is the value every timestamp gets stamped with, and
 * the first thing to check when a day's entries land on the wrong day.
 */
export function SettingsScreen() {
  const { clock, repos, storage } = useServices()
  const { status } = useStorageStatus()
  const { health } = useBackupHealth()
  const navigate = useNavigate()

  const [entries, setEntries] = useState<number | null>(null)
  const countEntries = useCallback(async () => {
    setEntries(await repos.entries.count())
  }, [repos])
  useEffect(() => {
    void countEntries()
  }, [countEntries])

  /*
   * `null` until read, so the switch never draws in the wrong position and then
   * corrects itself. A control that visibly flips on load reads as the app
   * changing the setting rather than reporting it.
   */
  const [preferences, setPreferences] = useState<Preferences | null>(null)
  useEffect(() => {
    void (async () => {
      setPreferences(await readPreferences(storage))
    })()
  }, [storage])

  async function setFinishStep(finishStep: boolean) {
    const next = { ...(preferences ?? DEFAULT_PREFERENCES), finishStep }
    setPreferences(next)
    await writePreferences(storage, next)
  }

  const now = clock.now()
  const wall = wallClockOf(now)
  const time = `${String(wall.hour).padStart(2, '0')}:${String(wall.minute).padStart(2, '0')}`

  return (
    <>
      <h1>Settings</h1>

      {/*
        Pinned above everything, and unlike the nag on Home it is here at every
        level rather than only past a threshold. This is the screen someone
        opens *to check*, so the answer has to be present even when it is a
        reassuring one — a notice that appears only when things are bad is one
        whose absence says nothing.
      */}
      {health !== null && (
        <section className={styles['backupCard']} data-testid="backup-notice">
          <Shield />
          <div className={styles['backupText']}>
            <p className={styles['backupHeadline']}>{health.headline}</p>
            {/*
              The consequence, from core. Never "your data is safe": persistence
              is a request the browser may refuse and may later revoke, and
              there is no copy anywhere else.
            */}
            <p className={styles['backupDetail']}>{health.detail}</p>
          </div>
          <Button variant="primary" onClick={() => void navigate('/backup')}>
            Back up
          </Button>
        </section>
      )}

      {/*
        First, and not really a setting.

        Tracks, check-ins and tags had no entry point anywhere in the app: the
        bottom navigation has five slots and none of them was one of these, and
        the only links were inside empty states that vanish the moment they stop
        being empty. A user could set up their first track from the home screen,
        create one check-in, and then have no route back to make a second — the
        screens were reachable by typing a URL and no other way.

        They live here because Settings is already in the navigation and is
        where people look for "manage the things I set up". The home screen
        carries a shortcut too, because that is where you are standing when you
        realise you want another check-in.
      */}
      {/*
        Above "Set up", because this changes what happens every time you log
        and the rows below it are things you touch once.
      */}
      <section className={styles['section']}>
        <h2 className={styles['sectionTitle']}>Logging</h2>
        <ListRow
          title="Finish with tags and a note"
          subtitle="A last step for the context you would otherwise have to remember later"
          trailing={
            <Toggle
              label="Finish with tags and a note"
              checked={preferences?.finishStep ?? DEFAULT_PREFERENCES.finishStep}
              disabled={preferences === null}
              onChange={(next) => void setFinishStep(next)}
            />
          }
        />
      </section>

      <section className={styles['section']}>
        <h2 className={styles['sectionTitle']}>Set up</h2>
        <ListRow
          title="Tracks"
          subtitle="The things you measure"
          onClick={() => void navigate('/tracks')}
        />
        <ListRow
          title="Check-ins"
          subtitle="Named sets of tracks you record together"
          onClick={() => void navigate('/templates')}
        />
        <ListRow
          title="Tags"
          subtitle="Labels you can filter your history by"
          onClick={() => void navigate('/tags')}
        />
        <ListRow
          title="Reminders"
          subtitle="Saved on this device — delivery arrives with the mobile app"
          onClick={() => void navigate('/reminders')}
        />
      </section>

      {/*
        Its own section, above storage rather than inside it, because "get my
        data out for a spreadsheet" and "make sure I do not lose my data" are
        different errands and this app must never let them be confused — a CSV
        is not a backup (ADR-0003).
      */}
      <section className={styles['section']}>
        <h2 className={styles['sectionTitle']}>Get your data out</h2>
        <ListRow
          title="Backup and restore"
          subtitle="A file that can be read back in. The only way to recover this data"
          onClick={() => void navigate('/backup')}
        />
        <ListRow
          title="Report"
          subtitle="A summary to print or hand to someone else"
          onClick={() => void navigate('/report')}
        />
        <ListRow
          title="Export as CSV"
          subtitle="A copy for a spreadsheet. Not a backup — it cannot be read back in"
          onClick={() => void navigate('/export')}
        />
      </section>

      <StorageSection status={status} health={health} />

      <section className={styles['section']}>
        <h2 className={styles['sectionTitle']}>This device</h2>
        <ListRow
          title="Time zone"
          subtitle="Recorded with every entry, so times stay correct across travel"
          trailing={
            <span className={styles['mono']} data-testid="time-zone">
              {now.timeZone}
            </span>
          }
        />
        <ListRow
          title="Local time"
          trailing={
            <span className={styles['mono']} data-testid="local-time">
              {time}
            </span>
          }
        />
        <ListRow
          title="Captured as"
          trailing={
            <span className={styles['mono']} data-testid="iso">
              {toIso8601(now)}
            </span>
          }
        />
        <ListRow
          title="Entries stored"
          subtitle="Everything recorded on this device"
          trailing={
            <span className={styles['mono']} data-testid="entry-count">
              {entries === null ? '…' : String(entries)}
            </span>
          }
        />
      </section>

      <section className={styles['section']}>
        <h2 className={styles['sectionTitle']}>Version</h2>
        <ListRow
          title="Core"
          trailing={
            <span className={styles['mono']} data-testid="core-version">
              {CORE_VERSION}
            </span>
          }
        />
        <ListRow
          title="Data schema"
          trailing={
            <span className={styles['mono']} data-testid="schema-version">
              {SCHEMA_VERSION}
            </span>
          }
        />
      </section>
    </>
  )
}

function Shield() {
  return (
    <svg
      className={styles['shield']}
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12 3l7 3v6c0 4-3 7.2-7 9-4-1.8-7-5-7-9V6z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Storage durability, stated plainly (T-205).
 *
 * "Your data is safe" is not a claim this app can make: persistence is a
 * request browsers may refuse and may later revoke, and there is no server copy
 * to fall back on. So this reports the actual state and one concrete thing to
 * do about it, which is also what makes the backup nag in T-503 land as
 * reasonable rather than as pestering.
 */
function StorageSection({
  status,
  health,
}: {
  readonly status: StorageStatus | null
  readonly health: BackupHealth | null
}) {
  if (!status) {
    return (
      <section className={styles['section']}>
        <h2 className={styles['sectionTitle']}>Storage</h2>
        <p className={styles['note']}>Checking…</p>
      </section>
    )
  }

  const { label, detail } = describePersistence(status.state)
  const { usageBytes, quotaBytes } = status

  return (
    <section className={styles['section']}>
      <h2 className={styles['sectionTitle']}>Storage</h2>
      <ListRow
        title="Durability"
        subtitle={detail}
        trailing={
          <span className={styles['mono']} data-testid="persistence-state">
            {label}
          </span>
        }
      />
      {/*
        Beside the durability state rather than on its own screen, because the
        two only mean anything together: "the browser may clear this" and "you
        last backed up five weeks ago" is one fact, and T-503 escalates on it.
      */}
      {health !== null && (
        <ListRow
          title="Last backup"
          subtitle={
            health.everBackedUp
              ? health.headline
              : 'Nothing on this device exists anywhere else'
          }
          trailing={
            <span className={styles['mono']} data-testid="last-backup">
              {!health.everBackedUp
                ? 'Never'
                : health.daysSince === 0
                  ? 'Today'
                  : `${health.daysSince}d ago`}
            </span>
          }
        />
      )}
      {usageBytes !== undefined && (
        <ListRow
          title="Used on this device"
          // Both numbers, not a percentage. A quota is typically gigabytes and
          // this app stores kilobytes, so every honest percentage rounds to 0%
          // and tells the user nothing.
          trailing={
            <span className={styles['mono']} data-testid="storage-usage">
              {formatBytes(usageBytes)}
              {quotaBytes === undefined ? '' : ` of ${formatBytes(quotaBytes)}`}
            </span>
          }
        />
      )}
    </section>
  )
}
